import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { ArrowRightIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Spinner,
} from '@monksflow/monks-ui';
import { analysesQuery, type AnalysisListItem, type AnalysisStatus } from '@/entities/analysis';
import { useUserDisplay } from '@/entities/user';
import { EmptyState } from '@/shared/ui/EmptyState';
import { TruncatedText } from '@/shared/ui/TruncatedText';
import { AnimatedTableRow } from '@/shared/ui/animations';
import { ROUTES } from '@/shared/config/routes';
import { formatRelativeDate } from '@/shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';

import styles from './IQAnalysisPreview.module.css';

const PREVIEW_ROW_LIMIT = 8;

/**
 * `completedAt` is optional on the DTO and typed as an opaque map rather than a
 * date, so `createdAt` is the only usable generation time. Ties break on `id` to
 * keep the order stable between renders.
 */
const byMostRecentlyGenerated = (a: AnalysisListItem, b: AnalysisListItem) => {
  const delta = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  return delta !== 0 ? delta : a.id.localeCompare(b.id);
};

/**
 * A finished analysis carries no marker at all — only the two states a reader
 * has to act on are labelled, so a full column would be blank on most rows.
 *
 * The status keeps full contrast while the rest of an unfinished row is muted:
 * it is the reason the row does not open, so dimming it with everything else
 * would hide the explanation.
 */
const RowStatus = ({ status }: { status: AnalysisStatus }) => {
  const { t } = useTranslation('analysis');

  if (status === 'completed') return null;

  if (status === 'failed') {
    return (
      <Badge variant="destructive" roundness="full" size="sm" className="shrink-0">
        {t('analysis.analysisTable.failed')}
      </Badge>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-2xs text-xs leading-5 font-normal text-muted-foreground">
      <Spinner size="sm" />
      {t('analysis.analysisTable.generating')}
    </span>
  );
};

/** `createdBy` is a user id on the wire, so the name needs a directory lookup. */
const CreatedByName = ({ userId }: { userId: string }) => {
  const { t } = useTranslation('analysis');
  const { name, isLoading } = useUserDisplay(userId);

  if (isLoading) return null;
  return <span className="truncate">{name ?? t('analysis.iqanalysisPreview.unknownCreator')}</span>;
};

export function IQAnalysisPreview() {
  const { t } = useTranslation('analysis');

  const navigate = useNavigate();
  const { data: analyses } = useQuery(analysesQuery());

  const recentAnalyses = [...(analyses ?? [])]
    .sort(byMostRecentlyGenerated)
    .slice(0, PREVIEW_ROW_LIMIT);

  return (
    <section className="content-block gap-md rounded-xl p-2xl" data-testid="home-iq-analysis">
      <div className="flex items-center justify-between">
        <h2 className="type-heading-sm leading-5 tracking-[-0.02em] text-foreground">
          {t('analysis.iqanalysisPreview.recentIqAnalysis')}
        </h2>
        <Button variant="ghost" size="sm" onClick={() => void navigate(ROUTES.IQ)}>
          {t('analysis.iqanalysisPreview.allAnalysis')}
          <ArrowRightIcon className="size-4" />
        </Button>
      </div>

      {recentAnalyses.length > 0 ? (
        <Datatable className={`w-full table-fixed ${styles.table}`}>
          <DatatableHeader>
            <DatatableRow>
              <DatatableHead scope="col" className="w-1/2 bg-transparent">
                {t('analysis.iqanalysisPreview.name')}
              </DatatableHead>
              <DatatableHead scope="col" className="w-1/4 bg-transparent">
                {t('analysis.iqanalysisPreview.createdBy')}
              </DatatableHead>
              <DatatableHead scope="col" className="w-1/4 bg-transparent">
                {t('analysis.iqanalysisPreview.createdAt')}
              </DatatableHead>
            </DatatableRow>
          </DatatableHeader>
          <DatatableBody>
            {recentAnalyses.map((analysis, index) => {
              const isCompleted = analysis.status === 'completed';

              return (
                <AnimatedTableRow
                  key={analysis.id}
                  index={index}
                  variant="datatable"
                  interactive={isCompleted}
                  data-testid={`analysis-row-${analysis.id}`}
                  // The datatable's row states are authored for a table sitting on
                  // `bg-background`. Here the cells are transparent over a card, and in
                  // dark mode `--muted` and `--card` are both stone-800, so the stock
                  // hover paints the colour already showing. Light mode still differs
                  // (white vs neutral-100), so it keeps the design system's own states.
                  className={
                    isCompleted
                      ? 'dark:hover:[&>*]:bg-surface-raised dark:active:[&>*]:bg-surface-emphasis'
                      : undefined
                  }
                  onClick={
                    isCompleted ? () => void navigate(ROUTES.IQ_ANALYSIS(analysis.id)) : undefined
                  }
                >
                  <DatatableCell className="bg-transparent">
                    <span className="flex min-w-0 items-center gap-sm">
                      {/* The row's click is a convenience; the link is what makes
                          the report reachable by keyboard, and what allows
                          middle-click and open-in-new-tab. */}
                      {isCompleted ? (
                        <Link
                          to={ROUTES.IQ_ANALYSIS(analysis.id)}
                          className="min-w-0 rounded-sm focus-visible:focus-ring"
                        >
                          <TruncatedText value={analysis.name} className="text-foreground" />
                        </Link>
                      ) : (
                        <TruncatedText value={analysis.name} className="text-muted-foreground" />
                      )}
                      <RowStatus status={analysis.status} />
                    </span>
                  </DatatableCell>

                  <DatatableCell
                    className={`bg-transparent ${isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}
                  >
                    <CreatedByName userId={analysis.createdBy} />
                  </DatatableCell>

                  <DatatableCell className="bg-transparent text-muted-foreground">
                    {formatRelativeDate(analysis.createdAt)}
                  </DatatableCell>
                </AnimatedTableRow>
              );
            })}
          </DatatableBody>
        </Datatable>
      ) : (
        <EmptyState title={t('analysis.iqanalysisPreview.noAnalysesYet')} />
      )}
    </section>
  );
}
